import { createSelector } from '@ngrx/store';

import { RecruitingDashboardAlertType } from 'src/app/recruiting/dashboard/enums/recruiting-dashboard-alert-type.enum';
import * as fromFeature from '../reducers';
import { RecruitingDashboardState } from '../state/recruiting-dashboard.state';
import { JobAlertDto } from '../../../shared/models/job-alert.model';
import { FilterValues, JobAlertRuleDisplay } from '../../../recruiting/dashboard/models/job-alert-rule-display.model';
import { DisplayJobAlertRule } from '../../../shared/models/display-job-alert-rule';
import { JobAlertColors } from '../../../recruiting/dashboard/models/job-alert-color';
import {
    ShiftTypeOptions,
    ShiftDurationOptions,
    ShiftNumberOptions
} from 'src/app/recruiting/dashboard/models/shift-constants';
import { convertHourToShiftTypeId } from 'src/app/recruiting/shared/utilities/shift-utils';

export const getRecruitingDashboardState = createSelector(
    fromFeature.recruitingFeature,
    (state: fromFeature.RecruitingRootStoreState) => state.dashboard
);

export const selectJobAlerts = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.jobAlerts
);

export const selectNewJobAlerts = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.newJobAlerts
);

export const selectLeadAssigned = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.leadAssignedTo
);

export const recruitingDashboardAlert = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.recruitingDashboardAlertData
);

export const recruitingDashboardJobAlert = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.recruitingDashboardJobAlertData
);

// Ryan B: Leverage combined selectors so that we can utilize memoization and make things
// a bit more common among the different tiles
export const selectRecruitingDashboardAlerts = (recruiterId: number, followupType: RecruitingDashboardAlertType) =>
    createSelector(
        getRecruitingDashboardState,
        selectShouldFetchData(recruiterId, followupType),
        (state, shouldFetch) => {
            return {
                shouldFetch,
                alerts: state.recruitingDashboardAlertData
            };
        }
    );

export const selectShouldFetchData = (recruiterId: number, alertType: RecruitingDashboardAlertType) =>
    createSelector(getRecruitingDashboardState, (state: RecruitingDashboardState) => {
        return (
            state.recruitingDashboardAlertData?.recruiterId === recruiterId &&
            (state.recruitingDashboardAlertData?.alertTypes.includes(alertType) ||
                state.recruitingDashboardAlertData.alertTypes.includes(RecruitingDashboardAlertType.AllTiles))
        );
    });

export const selectIsHubConnectionActive = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.isHubConnectionActive
);

export const selectJobAlertRules = createSelector(getRecruitingDashboardState, (state: RecruitingDashboardState) => {
    return state.jobAlertRules.map((rule: JobAlertRuleDisplay) => {
        const jobAlertRule: JobAlertRuleDisplay = {
            ...rule,
            textColor: JobAlertColors.find((color) => color.code === rule.color)?.textColor,
            empNames: rule.employmentTypeNames?.map((empType) => empType.name),
            profNames: rule.professionNames?.map((prof) => prof.name),
            specNames: rule.specialtyNames?.map((spec) => spec.name),
            locNames: rule.locationNames?.map((loc) => loc.name),
            shiftDurationNames: rule.filterValues.shiftDurations?.map(
                (duration) => ShiftDurationOptions.find((o) => o.key === duration)?.value
            ),
            shiftTypeNames: rule.filterValues.shiftTypeIds?.map(
                (type) => ShiftTypeOptions.find((o) => o.key === type)?.value
            ),
            shiftsNumberNames: rule.filterValues.shiftsNumber?.map(
                (number) => ShiftNumberOptions.find((o) => o.key === number)?.value
            )
        };
        return jobAlertRule;
    });
});

export const selectJobAlertsLastViewedAt = createSelector(
    getRecruitingDashboardState,
    (state: RecruitingDashboardState) => state.jobAlertsLastViewedAt
);

export const selectJobAlertsWithRules = createSelector(
    selectNewJobAlerts,
    selectJobAlertRules,
    selectJobAlertsLastViewedAt,
    (jobAlerts: Record<number, JobAlertDto>, jobAlertRules: JobAlertRuleDisplay[], alertsLastViewedAt?: string) => {
        const lastViewedAt = new Date(alertsLastViewedAt);

        if (jobAlertRules.length === 0) {
            return [] as JobAlertDto[];
        }

        const jobAlertsWithRules = Object.values(jobAlerts).map((jobAlert: JobAlertDto) => {
            const matchingJobAlertRules = jobAlertRules
                .filter((jobAlertRule) => hasMatchingFilter(jobAlert, jobAlertRule))
                .map((jobAlertRule) => {
                    const jobAlertColor = JobAlertColors.find((color) => color.code === jobAlertRule.color);
                    const newJobAlertRule: DisplayJobAlertRule = {
                        name: jobAlertRule.name,
                        color: jobAlertColor.hexCode,
                        textColor: jobAlertColor?.textColor
                    };
                    return newJobAlertRule;
                });

            const newJobAlert: JobAlertDto = {
                ...jobAlert,
                displayJobAlertRule: matchingJobAlertRules,
                showInBadge: new Date(jobAlert.enteredTime) > lastViewedAt
            };
            return newJobAlert;
        });
        return jobAlertsWithRules
            .filter((jobAlert) => jobAlert.displayJobAlertRule.length > 0)
            .sort((t1, t2) => (t1.enteredTime >= t2.enteredTime ? -1 : 1));
    }
);

export const selectNotViewedJobAlertsWithRules = createSelector(
    selectJobAlertsWithRules,
    (jobAlerts: JobAlertDto[]) => {
        return jobAlerts.filter((jobAlert: JobAlertDto) => jobAlert.showInBadge);
    }
);

const hasMatchingFilter = (jobAlert: JobAlertDto, rule: JobAlertRuleDisplay): boolean => {
    return (
        filterByEmploymentTypes(jobAlert, rule.filterValues) &&
        filterByProfessions(jobAlert, rule.filterValues) &&
        filterBySpecialties(jobAlert, rule.filterValues) &&
        filterByLocations(jobAlert, rule.filterValues) &&
        filterByStartDate(jobAlert, rule.filterValues) &&
        filterByEndDate(jobAlert, rule.filterValues) &&
        filterByPayRangeMin(jobAlert, rule.filterValues) &&
        filterByPayRangeMax(jobAlert, rule.filterValues) &&
        filterByShiftNum(jobAlert, rule.filterValues) &&
        filterByShiftHours(jobAlert, rule.filterValues) &&
        filterByShiftType(jobAlert, rule.filterValues)
    );
};

const filterByEmploymentTypes = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return !parsedFilterValues.employmentTypes || parsedFilterValues.employmentTypes.includes(job.employmentTypeCode);
};

const filterByProfessions = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return !parsedFilterValues.professions || parsedFilterValues.professions.includes(job.professionId);
};

const filterBySpecialties = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return (
        !parsedFilterValues.specialties ||
        parsedFilterValues.specialties.includes(job.expertiseId) ||
        (parsedFilterValues.includeRelatedSpecialties &&
            parsedFilterValues.relatedSpecialties?.includes(job.expertiseId))
    );
};

const filterByLocations = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return (
        !parsedFilterValues.actualLocations ||
        parsedFilterValues.actualLocations.length === 0 ||
        parsedFilterValues.actualLocations.includes(job.stateCode)
    );
};

const filterByStartDate = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return !parsedFilterValues.startDate || parsedFilterValues.startDate <= job.startDate;
};

const filterByEndDate = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return !parsedFilterValues.endDate || parsedFilterValues.endDate >= job.startDate;
};

const filterByPayRangeMin = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return !parsedFilterValues.payRangeMin || parsedFilterValues.payRangeMin <= job.regularPayLow;
};

const filterByPayRangeMax = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    return !parsedFilterValues.payRangeMax || parsedFilterValues.payRangeMax >= job.regularPayHigh;
};

const filterByShiftNum = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    // check against regular shift data, then against alternative shift if applicable
    return (
        !parsedFilterValues.shiftsNumber ||
        parsedFilterValues.shiftsNumber.includes(job.numberOfShifts) ||
        (parsedFilterValues.shiftIsAlternating && parsedFilterValues.shiftsNumber.includes(job.altNumberOfShifts))
    );
};

const filterByShiftHours = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    // check against regular shift data, then against alternative shift if applicable
    return (
        !parsedFilterValues.shiftDurations ||
        parsedFilterValues.shiftDurations.includes(job.shiftHours) ||
        (parsedFilterValues.shiftIsAlternating && parsedFilterValues.shiftDurations.includes(job.shiftAltHours))
    );
};

const filterByShiftType = (job: JobAlertDto, parsedFilterValues: FilterValues): boolean => {
    if (!parsedFilterValues.shiftTypeIds || parsedFilterValues.shiftTypeIds.length === 0) {
        return true;
    }

    // check against regular shift data, then against alternative shift if applicable
    return (
        !parsedFilterValues.shiftTypeIds ||
        parsedFilterValues.shiftTypeIds.includes(convertHourToShiftTypeId(job.shiftStartHour)) ||
        (parsedFilterValues.shiftIsAlternating &&
            parsedFilterValues.shiftTypeIds.includes(convertHourToShiftTypeId(job.shiftAltStartHour)))
    );
};
